/**
 * Krok UXR2 — TRETI OTAZKA KOKPITU: „Co zbývá?" (mockup `03-detail-zakazky.html`).
 *
 * Predloha ukazuje cestu „od náboru k penězům na účtu": hotove kroky
 * s datem, jeden krok „Teď" a sede kroky, ktere jeste nezacaly.
 *
 * ROZHODNUTI UXR2-2: kroky jsou SKUTECNE faze stavoveho automatu zakazky
 * (`KANBAN_STAGE_ORDER`, `packages/case/.../state-machine.ts`), ne devet
 * ilustrativnich nazvu z predlohy. Tentyz duvod jako ROZHODNUTI UXR1-3
 * u stitku fazi: vymyslet fazim nove nazvy znamena lhat o stavu zakazky, a
 * „Rezervační smlouva" by navic tvrdila neco jineho nez kanban vedle.
 *
 * Datum u hotoveho kroku je datum, kdy do nej zakazka VSTOUPILA — z udalosti
 * `stage_changed` na casove ose zakazky (`GET /v1/cases/:id/timeline`).
 * Kdyz udalost chybi (zakazka vznikla uz v pozdejsi fazi, napr. importem),
 * krok datum NEMA a nic se nedomysli.
 */

import { CANCELLED_STAGE, KANBAN_STAGE_ORDER, stageLabel } from './case-stages.js';
import type { CaseTimelineItem } from './cases-api.js';

export type JourneyState = 'done' | 'now' | 'later';

export interface JourneyStep {
  readonly stage: string;
  readonly label: string;
  readonly state: JourneyState;
  /** Kdy zakazka do faze vstoupila (ISO), kdyz to casova osa vi. */
  readonly enteredAt: string | null;
}

export interface CaseJourney {
  readonly steps: readonly JourneyStep[];
  readonly doneCount: number;
  readonly total: number;
  /** Podil hotovych kroku v procentech (cele cislo, pro `.rh-a2-bar`). */
  readonly pct: number;
  /** Zrusena zakazka cestu nema — ukoncila ji mimo pipeline. */
  readonly cancelled: boolean;
}

/**
 * Kdy zakazka vstoupila do jednotlive faze. Pri opakovanem vstupu (zakazka
 * se vratila zpet) plati POSLEDNI vstup — stejne pravidlo, jakym server
 * pocita `stageEnteredAt` (`case/src/internal/service.ts`).
 */
function enteredAtByStage(timeline: readonly CaseTimelineItem[]): ReadonlyMap<string, string> {
  const result = new Map<string, string>();
  for (const item of timeline) {
    if (item.kind !== 'stage_changed') continue;
    const payload = item.payload;
    if (payload === null || typeof payload !== 'object') continue;
    const to = (payload as Record<string, unknown>)['to'];
    if (typeof to !== 'string') continue;
    const previous = result.get(to);
    if (previous === undefined || previous < item.createdAt) result.set(to, item.createdAt);
  }
  return result;
}

export function caseJourney(
  stage: string,
  timeline: readonly CaseTimelineItem[],
  openedAt: string | null = null,
): CaseJourney {
  const entered = enteredAtByStage(timeline);
  const currentIndex = KANBAN_STAGE_ORDER.findIndex((item) => item === stage);
  const cancelled = stage === CANCELLED_STAGE;
  const steps = KANBAN_STAGE_ORDER.map((item, index): JourneyStep => {
    // Zrusena zakazka: zadny krok neni „Teď" (uz se nikam neposouva), hotove
    // zustavaji hotove — podle toho, kam zakazka skutecne dosla.
    const state: JourneyState =
      currentIndex < 0 || cancelled
        ? entered.has(item)
          ? 'done'
          : 'later'
        : index < currentIndex
          ? 'done'
          : index === currentIndex
            ? 'now'
            : 'later';
    return {
      stage: item,
      label: stageLabel(item),
      state,
      // Prvni faze zakazky zadnou udalost `stage_changed` nema — do ni se
      // vstoupilo zalozenim (`openedAt`).
      enteredAt: entered.get(item) ?? (index === 0 ? openedAt : null),
    };
  });
  const doneCount = steps.filter((step) => step.state === 'done').length;
  return {
    steps,
    doneCount,
    total: steps.length,
    pct: Math.round((doneCount / steps.length) * 100),
    cancelled,
  };
}
